/**
 * Returns and notes maths (ACCOUNTING_RULES §7) — pure, no React.
 *
 * The API does not expose "already returned" or "already credited" on invoice and bill lines, so they
 * are derived from the document's existing notes. The server re-checks everything (OVER_RETURN,
 * OVER_CREDIT); these figures guide the form and preview the credit.
 */
import { fromUnits, toUnits } from '../payments/allocation';

const ZERO = 0n;

/** A note line as far as these helpers care (credit and debit note lines both fit). */
export interface NoteLineLike {
  /** The invoice or bill line it credits. */
  lineId: string;
  /** Returned base units; null for a price-only credit. */
  qtyBase: string | null;
  lineTotal: string;
}

export interface NoteLike {
  status: string;
  lines: readonly NoteLineLike[];
}

export interface LineUsage {
  /** Base units already returned through returns' notes. */
  returnedBase: bigint;
  /** Gross amount already credited (returns and price adjustments). */
  credited: bigint;
}

/** Per source line: what posted notes already returned and credited (cancelled notes don't count). */
export function usageByLine(notes: readonly NoteLike[]): Map<string, LineUsage> {
  const out = new Map<string, LineUsage>();
  for (const note of notes) {
    if (note.status !== 'POSTED') continue;
    for (const l of note.lines) {
      const u = out.get(l.lineId) ?? { returnedBase: ZERO, credited: ZERO };
      u.returnedBase += toUnits(l.qtyBase) ?? ZERO;
      u.credited += toUnits(l.lineTotal) ?? ZERO;
      out.set(l.lineId, u);
    }
  }
  return out;
}

/** Ten-thousandths of a line-unit quantity from base units and the unit factor (rounded down). */
export function baseToLineUnits(base: bigint, factor: string): bigint {
  const f = toUnits(factor);
  if (!f || f <= ZERO) return base;
  // base and factor both carry 4 decimals: (base / 10^4) / (f / 10^4) → × 10^4 for the result.
  return (base * 10_000n) / f;
}

/** What is left to return on a line, in the line unit: qty − returned (never negative). */
export function returnable(qty: string, factor: string, returnedBase: bigint): bigint {
  const left = (toUnits(qty) ?? ZERO) - baseToLineUnits(returnedBase, factor);
  return left > ZERO ? left : ZERO;
}

/** What is left to credit on a line: lineTotal − credited (never negative). */
export function creditable(lineTotal: string, credited: bigint): bigint {
  const left = (toUnits(lineTotal) ?? ZERO) - credited;
  return left > ZERO ? left : ZERO;
}

/** Rounds ten-thousandths to `decimals`, half up (money preview). */
export function roundUnits(units: bigint, decimals: number): bigint {
  const step = 10n ** BigInt(4 - Math.min(4, Math.max(0, decimals)));
  if (step === 1n) return units;
  const neg = units < ZERO;
  const abs = neg ? -units : units;
  const r = ((abs + step / 2n) / step) * step;
  return neg ? -r : r;
}

/**
 * Credit for returning `qty` of a line: its share of the line total (net + tax), rounded half up.
 * The server splits by cumulative quantity, so the last return of a line absorbs rounding — this is
 * a preview.
 */
export function estimateLineCredit(
  lineTotal: string,
  lineQty: string,
  qty: string,
  decimals = 2,
): bigint {
  const total = toUnits(lineTotal) ?? ZERO;
  const of = toUnits(lineQty) ?? ZERO;
  const q = toUnits(qty) ?? ZERO;
  if (of <= ZERO || q <= ZERO) return ZERO;
  return roundUnits((total * q) / of, decimals);
}

/** One editable row of a return or note form. */
export interface DraftLine {
  lineId: string;
  /** Upper bound (ten-thousandths): returnable qty or creditable amount. */
  max: bigint;
  value: string;
}

export interface DraftCheck {
  errors: Record<string, string>;
  /** Rows with a positive value. */
  count: number;
  valid: boolean;
}

/**
 * Validates a return/note draft: each value is a plain decimal (≤ `scale` decimals), within its
 * bound; at least one row is filled.
 */
export function checkDraft(
  rows: readonly DraftLine[],
  { scale = 4, overMessage = 'More than is left' }: { scale?: number; overMessage?: string } = {},
): DraftCheck {
  const errors: Record<string, string> = {};
  let count = 0;
  for (const r of rows) {
    const raw = r.value.trim();
    if (raw === '') continue;
    const units = /^\d{1,15}(\.\d{1,4})?$/.test(raw) ? toUnits(raw) : null;
    if (units === null) {
      errors[r.lineId] = 'Enter a number';
      continue;
    }
    if ((raw.split('.')[1]?.length ?? 0) > scale) {
      errors[r.lineId] = `At most ${String(scale)} decimals`;
      continue;
    }
    if (units > r.max) errors[r.lineId] = overMessage;
    if (units > ZERO) count += 1;
  }
  return { errors, count, valid: count > 0 && Object.keys(errors).length === 0 };
}

/** Formats ten-thousandths as a plain quantity string ("3", "2.5"). */
export function unitsToQty(units: bigint): string {
  return fromUnits(units, 0);
}

/**
 * A quantity in another unit of the same item (ten-thousandths in, ten-thousandths out): 0.5 box
 * at 100 pc a box is 50 pc. Rounds down, so a converted maximum never exceeds the original.
 */
export function convertUnits(qty: bigint, fromFactor: string, toFactor: string): bigint {
  const from = toUnits(fromFactor) ?? 10_000n;
  const to = toUnits(toFactor) ?? 10_000n;
  if (from <= ZERO || to <= ZERO) return qty;
  return (qty * from) / to;
}
